import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { AgricolaDependantDialogComponent } from '../agricola-dependent-dialog/agricola-dependent-dialog.component';
import {
  AgricolaToastType,
  InsuranceDependent,
  RelationshipCatalogStrategy,
  RelationshipOption
} from '../../../../../interfaces/agricola.interface';
import { AgricolaService } from '../../../../../services/agricola.service';
import {
  AGRICOLA_MINOR_IDENTIFICATION_TYPE,
  agricolaIsMinor,
  agricolaMinorIdentificationId
} from '../../../agricola-validators';

@Component({
  selector: 'app-agricola-dependants-step',
  templateUrl: './agricola-dependants-step.component.html',
  styleUrls: ['./agricola-dependants-step.component.css'],
  standalone: false
})
export class AgricolaDependantsStepComponent implements OnInit, OnDestroy {

  @Input() group!: FormGroup;
  @Input() selectedIndex: number = 0;
  @Input() steps: string[] = [];
  @Input() productCode = '';
  @Input() insuredIdentificationId = '';
  @Input() relationshipStrategy: RelationshipCatalogStrategy = 'global';
  @Input() maxDependants: number | null = null;
  @Output() nextStep = new EventEmitter<number>();
  @Output() previousStep = new EventEmitter<void>();

  showSuccessBanner = false;
  successMessage = 'Tu dependiente se agregó correctamente. Verifica la información y continúa al siguiente paso.';
  showErrorBanner = false;
  errorMessage = '';
  toastMessage: string = '';
  toastType: AgricolaToastType = 'error';
  showToast: boolean = false;
  loadingRelationships = false;
  relationshipsLoadedKey = 0;
  relationshipsLoadFailed = false;
  relationshipOptions: RelationshipOption[] = [];
  private readonly relationshipOptionsSubject = new BehaviorSubject<RelationshipOption[]>([]);
  private readonly relationshipLoadingSubject = new BehaviorSubject<boolean>(false);
  private readonly relationshipLoadedKeySubject = new BehaviorSubject<number>(0);
  private readonly relationshipLoadFailedSubject = new BehaviorSubject<boolean>(false);
  private relationshipSubscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private agricolaService: AgricolaService
  ) {}

  ngOnInit(): void {
    this.loadRelationshipOptions();
  }

  ngOnDestroy(): void {
    this.relationshipSubscription.unsubscribe();
    this.relationshipOptionsSubject.complete();
    this.relationshipLoadingSubject.complete();
    this.relationshipLoadedKeySubject.complete();
    this.relationshipLoadFailedSubject.complete();
  }

  get dependants(): FormArray {
    return this.group.get('dependants') as FormArray;
  }

  get isAssistance(): boolean {
    return this.agricolaService.isAssistanceProduct;
  }

  get productTitle(): string {
    return this.agricolaService.getProductBySelection(this.productCode)?.product_name || 'Producto';
  }

  retryLoadRelationships(): void { this.loadRelationshipOptions(); }

  private loadRelationshipOptions(): void {
    this.relationshipsLoadFailed = false;
    this.loadingRelationships = true;
    this.relationshipLoadingSubject.next(true);
    this.relationshipLoadFailedSubject.next(false);
    this.relationshipSubscription = this.agricolaService.getRelationshipOptions(this.relationshipStrategy, this.productCode)
      .pipe(finalize(() => {
        this.loadingRelationships = false;
        this.relationshipLoadingSubject.next(false);
      }))
      .subscribe({
        next: options => {
          this.relationshipOptions = options;
          this.relationshipOptionsSubject.next(options);
          this.relationshipsLoadedKey++;
          this.relationshipLoadedKeySubject.next(this.relationshipsLoadedKey);
        },
        error: () => {
          this.relationshipOptions = [];
          this.relationshipOptionsSubject.next([]);
          this.relationshipsLoadFailed = true;
          this.relationshipLoadFailedSubject.next(true);
          this.showToastMessage('No fue posible cargar el catálogo de parentescos.', 'error');
        }
      });
  }

  get effectiveMaxDependants(): number {
    return this.maxDependants !== null ? this.maxDependants : Infinity;
  }

  private openDialog(mode: 'add' | 'edit', index?: number): void {
    if (mode === 'add' && this.dependants.length >= this.effectiveMaxDependants) {
      const limit = this.maxDependants !== null
        ? `Puedes agregar como máximo ${this.maxDependants} dependiente${this.maxDependants === 1 ? '' : 's'}.`
        : 'Se ha alcanzado el máximo de dependientes permitido.';
      this.showToastMessage(limit, 'warning');
      return;
    }

    if (!this.loadingRelationships && !this.relationshipOptions.length) {
      this.showToastMessage('El catálogo de parentescos no está disponible. Intenta nuevamente.', 'error');
      return;
    }

    let current: InsuranceDependent | null = null;

    if (mode === 'edit' && index !== undefined) {
      const ctrl = this.dependants.at(index) as FormGroup;
      current = ctrl.value as InsuranceDependent;
    }

    const existingIdentificationIds = this.dependants.controls
      .map((ctrl, currentIndex) => currentIndex === index ? '' : String(ctrl.get('identification_id')?.value || ''))
      .filter(Boolean);

    const existingRelationships = this.dependants.controls
      .map((ctrl, currentIndex) => currentIndex === index ? '' : String(ctrl.get('relationship')?.value || ''))
      .filter(Boolean);

    const dialogRef = this.dialog.open(AgricolaDependantDialogComponent, {
      width: '560px',
      disableClose: true,
      data: {
        mode,
        dependent: current,
        relationshipOptions: this.relationshipOptions,
        relationshipOptions$: this.relationshipOptionsSubject.asObservable(),
        relationshipLoading$: this.relationshipLoadingSubject.asObservable(),
        relationshipLoadedKey$: this.relationshipLoadedKeySubject.asObservable(),
        relationshipLoadFailed$: this.relationshipLoadFailedSubject.asObservable(),
        retryRelationship: () => this.retryLoadRelationships(),
        existingIdentificationIds,
        existingRelationships,
        insuredIdentificationId: this.insuredIdentificationId
      }
    });

    dialogRef.afterClosed().subscribe((result: InsuranceDependent | undefined) => {
      if (!result) {
        return;
      }

      if (mode === 'add') {
        this.dependants.push(this.createDependantGroup(result));
        this.successMessage = 'Tu dependiente se agregó correctamente. Verifica la información y continúa al siguiente paso.';
      } else if (mode === 'edit' && index !== undefined) {
        this.dependants.at(index).patchValue(result);
        this.successMessage = 'Tu dependiente se actualizó correctamente.';
      }

      this.syncDependantState();
      this.showSuccessBanner = true;
      this.showErrorBanner = false;
      this.errorMessage = '';
    });
  }

  createDependantGroup(d: InsuranceDependent): FormGroup {
    return this.fb.group({
      name: [d.name || ''],
      first_name: [d.first_name || ''],
      middle_name: [d.middle_name || ''],
      last_name: [d.last_name || ''],
      last_name_2: [d.last_name_2 || ''],
      gender: [d.gender ?? null],
      identification_type: [d.identification_type ?? null],
      identification_id: [d.identification_id || ''],
      birthday: [d.birthday || ''],
      relationship: [d.relationship || '']
    });
  }

  private syncDependantState(): void {
    this.applyMinorIdentifications();
    this.agricolaService.setCurrentInsuranceDependent(this.dependants.value as InsuranceDependent[]);
  }

  // Minors never capture a document: it is generated from the holder's
  // identification plus their correlative among the minors of the policy
  // (543455 -> 543455_1D, 543455_2D...). Recalculated on every change so the
  // numbering stays contiguous after edits or deletions.
  private applyMinorIdentifications(): void {
    let minorSequence = 0;
    this.dependants.controls.forEach(ctrl => {
      if (!agricolaIsMinor(ctrl.get('birthday')?.value)) {
        return;
      }

      minorSequence++;
      ctrl.get('identification_type')?.setValue(AGRICOLA_MINOR_IDENTIFICATION_TYPE, { emitEvent: false });
      ctrl.get('identification_id')?.setValue(
        agricolaMinorIdentificationId(this.insuredIdentificationId, minorSequence),
        { emitEvent: false }
      );
    });
  }

  private validateBeforeContinue(): boolean {
    this.applyMinorIdentifications();

    if (this.dependants.length === 0) {
      this.errorMessage = '';
      this.syncDependantState();
      return true;
    }

    const normalizedIds = this.dependants.controls.map(ctrl => this.normalizeId(ctrl.get('identification_id')?.value));
    const hasDuplicates = new Set(normalizedIds).size !== normalizedIds.length;
    if (hasDuplicates) {
      this.errorMessage = 'No puedes agregar dependientes con el mismo número de identificación.';
      return false;
    }

    if (normalizedIds.includes(this.normalizeId(this.insuredIdentificationId))) {
      this.errorMessage = 'El dependiente no puede tener el mismo número de identificación del asegurado.';
      return false;
    }

    const hasMinorWithoutIdentification = this.dependants.controls.some(ctrl =>
      agricolaIsMinor(ctrl.get('birthday')?.value) &&
      !String(ctrl.get('identification_id')?.value || '').trim()
    );

    if (hasMinorWithoutIdentification) {
      this.errorMessage = 'No fue posible generar la identificación de los dependientes menores de edad: falta la identificación del titular.';
      return false;
    }

    const hasMissingRequired = this.dependants.controls.some(ctrl =>
      !String(ctrl.get('first_name')?.value || '').trim() ||
      !String(ctrl.get('last_name')?.value || '').trim() ||
      ctrl.get('gender')?.value == null ||
      ctrl.get('gender')?.value === '' ||
      !String(ctrl.get('identification_id')?.value || '').trim() ||
      ctrl.get('identification_type')?.value == null ||
      ctrl.get('identification_type')?.value === '' ||
      !String(ctrl.get('birthday')?.value || '').trim() ||
      !String(ctrl.get('relationship')?.value || '').trim()
    );

    if (hasMissingRequired) {
      this.errorMessage = 'Por favor, completa todos los campos obligatorios de cada dependiente.';
      return false;
    }

    this.errorMessage = '';
    this.syncDependantState();
    return true;
  }

  private normalizeId(value: unknown): string {
    return String(value || '').trim().toUpperCase();
  }

  getRelationshipLabel(value: unknown): string {
    const code = String(value || '');
    return this.relationshipOptions.find(option => option.value === code)?.label || code;
  }

  onAddDependant(): void {
    this.openDialog('add');
  }

  onEditDependant(index: number): void {
    this.openDialog('edit', index);
  }

  onDeleteDependant(index: number): void {
    this.dependants.removeAt(index);
    this.syncDependantState();
    this.showSuccessBanner = false;
  }

  closeSuccessBanner(): void {
    this.showSuccessBanner = false;
  }

  hideToast(): void {
    this.showToast = false;
  }

  showToastMessage(message: string, type: AgricolaToastType = 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.showToast = true;
  }

  onContinueClick(): void {
    if (!this.validateBeforeContinue()) {
      this.showErrorBanner = true;
      this.showSuccessBanner = false;
      return;
    }

    this.showErrorBanner = false;
    const nextIndex = this.selectedIndex + 1;
    this.nextStep.emit(nextIndex);
  }

  goBack(): void {
    this.previousStep.emit();
  }

  goToStep(index: number): void {
    this.nextStep.emit(index);
  }
}
